import CancelIcon from '@mui/icons-material/Cancel';
import { useCallback } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';

import { GetInstrumentsScheduledEditsItem } from '@Common/types/bo/instruments_scheduled_edits';

import { Button } from '@sh4res/bo-common/components';
import { useConfirmDialog, useUpdatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

type ScheduledEditCancelButtonProps = {
  scheduledEdit: GetInstrumentsScheduledEditsItem;
  'data-testid'?: string;
};

export const ScheduledEditCancelButton = ({
  scheduledEdit,
  'data-testid': dataTestId,
}: ScheduledEditCancelButtonProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const [cancelScheduledEdit, { isLoading }] = useUpdatePromise<
    Record<string, never>,
    { id: number }
  >();

  const confirmDialog = useConfirmDialog({
    title: 'resources.bo_common.instruments/scheduled_edits.cancel.title',
    content: 'resources.bo_common.instruments/scheduled_edits.cancel.content',
    showCancelX: true,
  });

  const isCancellable = scheduledEdit.status === 'pending';

  const openConfirmModal = useCallback(async () => {
    const confirmed = await confirmDialog.confirm();
    if (!confirmed) {
      return;
    }

    try {
      await cancelScheduledEdit('instruments/scheduled_edits/cancel', {
        id: scheduledEdit.id,
        data: {},
        previousData: scheduledEdit,
      });

      notify('resources.bo_common.instruments/scheduled_edits.cancel.success', { type: 'success' });
      refresh();
    } catch (error) {
      notify('resources.bo_common.instruments/scheduled_edits.cancel.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    }
  }, [confirmDialog, cancelScheduledEdit, notify, refresh, scheduledEdit]);

  return (
    <>
      <Button
        data-testid={dataTestId}
        permissions={{ resource: 'instruments/scheduled_edits/cancel', action: 'edit' }}
        color="error"
        variant="contained"
        size="small"
        disabled={!isCancellable || isLoading}
        onClick={openConfirmModal}
        label={translate('resources.bo_common.instruments/scheduled_edits.cancel.button')}>
        <CancelIcon />
      </Button>
      {confirmDialog.Component}
    </>
  );
};
